/**
 * Actor v3 LangChain Chat API クライアント
 * https://api.actor.gftd.ai/v3/langchain/actor を使用したチャット機能を提供
 */

export interface ChatMessage {
  id: string
  role: "user" | "assistant" | "system"
  content: string
  timestamp: Date
}

export interface LangChainActorConfig {
  name: string
  description?: string
  model?: "gpt-4o" | "gpt-4o-mini" | "gpt-4-turbo" | "gpt-4" | "gpt-3.5-turbo"
  temperature?: number
  max_tokens?: number
  system_prompt?: string
  organization_id: string
  openai_api_key?: string
  max_history_length?: number
  actor_id?: string
}

export interface LangChainActorResponse {
  actor_id: string
  actor: any // ActorState
  config: LangChainActorConfig
  endpoints: {
    chat: string
    system_prompt: string
    config_update: string
    stats: string
  }
}

export interface ChatRequest {
  message: string
  conversation_id?: string
  timeout?: number
  stream?: boolean
}

export interface ChatResponse {
  content: string
  usage: {
    total_tokens: number
    prompt_tokens: number
    completion_tokens: number
  }
  model: string
  conversation_id: string
  execution_time_ms: number
  timestamp: string
}

/**
 * Actor v3 LangChain Chat API クライアントクラス
 * Actor v3のLangChain統合機能を使用したチャット機能を提供
 */
export class ActorV3ChatClient {
  private baseUrl: string
  private headers: HeadersInit
  private organizationId: string
  private currentActorId: string | null = null

  constructor(
    baseUrl = "https://actor.gftd.ai/api/v3",
    organizationId = "actors",
    apiKey?: string
  ) {
    this.baseUrl = baseUrl.replace(/\/$/, "") // 末尾のスラッシュを削除
    this.organizationId = organizationId
    this.headers = {
      "Content-Type": "application/json",
      "Accept": "application/json",
    }

    if (apiKey) {
      this.headers["Authorization"] = `Bearer ${apiKey}`
    }
  }

  /**
   * LangChain Actor を作成
   * @param config LangChain Actor設定
   * @returns LangChain Actor作成レスポンス
   */
  async createLangChainActor(config: Partial<LangChainActorConfig>): Promise<LangChainActorResponse> {
    try {
      const actorConfig: LangChainActorConfig = {
        name: config.name || "GFTD Chat Assistant",
        description: config.description || "AI会話アシスタント",
        model: config.model || "gpt-3.5-turbo",
        temperature: config.temperature || 0.7,
        max_tokens: config.max_tokens || 1000,
        system_prompt: config.system_prompt || "あなたは親切で知識豊富なAIアシスタントです。",
        organization_id: this.organizationId,
        max_history_length: config.max_history_length || 50,
        ...config,
      }

      const response = await fetch(`${this.baseUrl}/langchain/actor`, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify(actorConfig),
      })

      if (!response.ok) {
        throw new Error(`LangChain Actor作成エラー: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      this.currentActorId = data.actor_id
      return data as LangChainActorResponse
    } catch (error) {
      console.error("LangChain Actor作成エラー:", error)
      throw error
    }
  }

  /**
   * チャットメッセージを送信
   * @param actorId Actor ID
   * @param request チャットリクエスト
   * @returns チャットレスポンス
   */
  async sendChatMessage(actorId: string, request: ChatRequest): Promise<ChatResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/langchain/actor/${actorId}/chat`, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        throw new Error(`チャットメッセージ送信エラー: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      return data as ChatResponse
    } catch (error) {
      console.error("チャットメッセージ送信エラー:", error)
      throw error
    }
  }

  /**
   * 現在のActorでチャット（簡易版）
   * @param message メッセージ内容
   * @param conversationId 会話ID（オプション）
   * @returns チャットレスポンス
   */
  async chat(message: string, conversationId?: string): Promise<ChatResponse> {
    // Actorが存在しない場合は作成
    if (!this.currentActorId) {
      await this.createLangChainActor({})
    }

    if (!this.currentActorId) {
      throw new Error("LangChain Actorの作成に失敗しました")
    }

    return this.sendChatMessage(this.currentActorId, {
      message,
      conversation_id: conversationId || "default-conversation",
    })
  }

  /**
   * システムプロンプトを更新
   * @param actorId Actor ID
   * @param systemPrompt 新しいシステムプロンプト
   */
  async updateSystemPrompt(actorId: string, systemPrompt: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/langchain/actor/${actorId}/system-prompt`, {
        method: "PUT",
        headers: this.headers,
        body: JSON.stringify({ system_prompt: systemPrompt }),
      })

      if (!response.ok) {
        throw new Error(`システムプロンプト更新エラー: ${response.status} ${response.statusText}`)
      }
    } catch (error) {
      console.error("システムプロンプト更新エラー:", error)
      throw error
    }
  }

  /**
   * APIの健全性をチェック
   * @returns APIが利用可能かどうか
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/actor`, {
        method: "GET",
        headers: { "Accept": "application/json" },
        // タイムアウトを追加
        signal: AbortSignal.timeout(5000)
      })
      return response.ok
    } catch (error) {
      // ネットワークエラーは静かに処理
      return false
    }
  }

  /**
   * 現在のActor IDを取得
   * @returns 現在のActor ID
   */
  getCurrentActorId(): string | null {
    return this.currentActorId
  }

  /**
   * Actor IDを手動設定
   * @param actorId 設定するActor ID
   */
  setCurrentActorId(actorId: string): void {
    this.currentActorId = actorId
  }
}

/**
 * デフォルトのActor v3 Chat APIクライアントインスタンス
 */
export const actorV3ChatClient = new ActorV3ChatClient() 